// Lightweight i18n with EN/HI dictionaries — persisted in localStorage.
import React, { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: {
      home: "Home", plans: "Naksha (House Plan)", pricing: "Pricing", customize: "Customize",
      blog: "Blog", contact: "Contact", login: "Login", signup: "Sign Up",
      dashboard: "Dashboard", logout: "Logout", partnerWith: "Partner with us",
      services: "Services", becomePartner: "Become a Partner",
      more: "More", build: "Build a home", about: "About",
    },
    hero: {
      tagline: "Instant plans • BOQ • Cost estimates • Verified partners",
      h1a: "Get Your House Plan in",
      h1b: "2 Minutes",
      sub: "Plot dimensions in. Instant plans, BOQ, and cost estimates out. Plus expert support to build it right.",
      length: "Length (ft)", width: "Width (ft)", floors: "Floors",
      facing: "Road Facing", findPlan: "Find My Plan",
      vastuToggle: "All NakshaCart plans are designed as per Vastu principles as far as possible.",
      facingLabel: "Facing",
      surroundingsToggle: "What's adjacent to your plot?",
      surroundingsOptional: "(optional)",
      surroundingsHelp: "Helps us recommend Vastu-friendly layouts. We'll save this with your enquiry.",
      chip1: "Free 2 bundles", chip2: "BOQ included", chip3: "EN + हिंदी",
    },
    facing: { N: "North", S: "South", E: "East", W: "West", NE: "North-East", NW: "North-West", SE: "South-East", SW: "South-West", ANY: "Any" },
    cta: { talkExpert: "Talk to Expert", getPlan: "Get Plan Now", customize: "Customize this Plan", needHelp: "Need a custom design?" },
    sections: {
      featured: "Featured Plans", featuredSub: "Hand-picked layouts loved by builders across India.",
      categories: "Browse by Category", categoriesSub: "Find a starting point that fits your plot and lifestyle.",
      how: "How NakshaCart Works", howSub: "From plot to plan to partner — in four steps.",
      stats: "Trusted by builders across India",
      whyUs: "Why NakshaCart", whyUsSub: "Designed for the way Indians actually build their homes.",
      states: "Find Plans in Your State",
      testimonials: "Customer Stories",
      partners: "Meet our verified partners", partnersSub: "Architects, civil engineers and contractors — vetted by us.",
      faq: "Frequently Asked Questions",
      leadTitle: "Not finding the right plan?",
      leadSub: "Our experts will charge very nominal fees for customised solutions.",
      featuredAction: "View all",
      asFeaturedIn: "As featured in",
    },
    how: {
      s1: "Enter your plot", s1d: "Length, width, floors, road-facing — that's it.",
      s2: "See matched plans", s2d: "We find the closest plans from our verified library.",
      s3: "Download free bundle", s3d: "Floor Plan + BOQ + Cost Estimate.",
      s4: "Connect with experts", s4d: "and discuss your requirements, we will together materialise your concept.",
      step: "STEP",
    },
    why: {
      one: "Instant Plans", oneD: "For your family discussions & finalising requirement & concept.",
      two: "Plan Finalisation", twoD: "We draw a plan for your house after your discussion. You can still modify your plan unlimited number of times based on your concept.",
      three: "BOQ + Cost Transparency", threeD: "BOQ & cost estimates will also be provided based on your finalised plan. 3D model & other drawings will also be prepared and finalised based on your plan.",
    },
    plan: {
      bhk: "BHK", floors: "Floors", area: "Built-up", facing: "Facing",
      view: "View Details", download: "Download",
      floorPlan: "Floor Plan", boq: "Bill of Quantities", cost: "Cost Estimate", cad: "AutoCAD Drawing",
      locked: "Upgrade to Download", premium: "Premium",
    },
    leadForm: {
      name: "Your name", mobile: "Mobile number", email: "Email (optional)",
      message: "Tell us about your plot or project",
      submit: "Call Me Back", success: "Thanks! Our team will reach out within 24 hours.",
      thankYou: "Thank you",
      plotLength: "Plot Length (ft)", plotWidth: "Plot Width (ft)",
      plotLengthPh: "e.g. 30", plotWidthPh: "e.g. 40",
      facingLabel: "Road Facing Direction", selectFacing: "Select facing direction",
      surroundings: "What is on each side of your plot?",
      state: "State", district: "District",
      statePh: "Maharashtra", districtPh: "Pune",
      disclaimer: "By submitting, you agree to be contacted by NakshaCart partners. We never sell your info.",
      errRequired: "Name and mobile are required",
    },
    compass: {
      select: "Select…", yourPlot: "Your Plot",
      side: { road: "Road", anotherPlot: "Another Plot", openLand: "Open Land", pond: "Pond / Water Body", nala: "Nala / Drain", building: "Building", garden: "Garden / Farm", other: "Other" },
    },
    footer: {
      tagline: "Plans in 2 minutes. Built right with verified partners.",
      product: "Product", company: "Company", legal: "Legal",
      about: "About", services: "Services", terms: "Terms", privacy: "Privacy",
      copyright: "© 2026 NakshaCart. Designed in India for India.",
      affiliate: "Affiliate Program",
      legalAll: "All legal policies",
      refund: "Refund Policy", cancellation: "Cancellation Policy", delivery: "Delivery Policy",
      adminLogin: "Admin login",
      careers: "Careers",
    },
    auth: {
      mobile: "Mobile Number", password: "Password", name: "Full Name", email: "Email (optional)",
      loginTitle: "Welcome back", loginSub: "Login to download plans, manage orders and connect with partners.",
      signupTitle: "Create your account", signupSub: "Free 2 plan downloads, lifetime.",
      signupCta: "Create Account", loginCta: "Login", noAccount: "Don't have an account?", hasAccount: "Already have an account?",
      partnerCta: "Become a Partner", forgotPassword: "Forgot password?",
    },
    contact: {
      title: "Contact NakshaCart",
      sub: "Questions about a plan, partnerships, or custom designs? We'd love to hear from you.",
      phone: "Phone", email: "Email", hq: "HQ", hqValue: "Bengaluru, India",
    },
    plansPage: {
      title: "House Plans Library",
      sub: "Browse {n}+ verified plans, or use filters to narrow down.",
      anyFacing: "Any facing", anyFloors: "Any floors", anyBhk: "Any BHK",
      allCategories: "All categories",
      catBudget: "Budget", catDuplex: "Duplex", catLuxury: "Luxury", catVastu: "Vastu",
      clearAll: "Clear all",
      matchInfo: "Showing {count} matched plans for {length}×{width} ft, {floors} floors, {facing} facing.",
      noMatchTitle: "Couldn't find your perfect plan?",
      noMatchSub: "Get a custom design from our experts — drafted for your exact plot in 5–7 working days.",
      floorsOpt: "{n} floors", bhkOpt: "{n} BHK",
    },
    planDetail: {
      backToPlans: "Back to plans",
      clickToZoom: "Click to zoom",
      productDescription: "Product Description",
      floorDescription: "Floor Description",
      plotDetails: "Plot Details",
      vastuCompliant: "Vastu compliant",
      estCost: "Estimated Cost of Construction",
      customize: "Customize this Plan",
      downloadBundle: "Download Bundle",
      bundleNote: "Pay-per-PDF • Watermarked • GST 18% added at checkout",
      pdfWatermarked: "PDF • Watermarked",
      inCart: "In cart", add: "Add",
      addToCart: "Add to Cart", buyNow: "Buy Now",
      viewCart: "View cart ({n}) →",
      specWidth: "Width", specLength: "Length / Depth", specPlotArea: "Plot Area",
      specFloors: "Floors", specBuiltUp: "Built-up Area", specFacing: "Facing",
      specCategory: "Category", specEstCost: "Est. Cost of Construction",
      roomBedrooms: "Bedrooms", roomLiving: "Living Room", roomBathroom: "Bathroom", roomKitchen: "Kitchen",
    },
    planCard: {
      featured: "Featured", from: "From", gst: "+ 18% GST",
    },
    pricing: {
      chip: "Pay only for the PDFs you need",
      title: "Simple, per-PDF pricing",
      sub: "Every plan lists individual prices for Floor Plan, BOQ and Cost Estimate. Add what you need to your cart, pay once, download forever. {gst}% GST applies on all paid items.",
      free: "Free",
      floorPlanLabel: "Floor Plan", floorPlanNote: "Detailed dimensioned floor layout — your primary blueprint.",
      boqLabel: "Bill of Quantities", boqNote: "Line-by-line list of materials needed — eliminates contractor padding.",
      costLabel: "Cost Estimate", costNote: "Expected construction cost broken down by item and stage.",
      freeAvailable: "Some plans include this for free.",
      feature1: "Watermarked PDFs delivered instantly",
      feature2: "Transparent {gst}% GST — added at checkout",
      feature3: "GSTIN invoice for businesses",
      ctaTitle: "Need a custom design or a plot bigger than 2000 sqft?",
      ctaSub: "Talk to our experts — first consultation is free.",
      browse: "Browse Plans", talk: "Talk to Us",
    },
    customize: {
      chip: "Experts",
      title: "Get a plan designed for your plot",
      sub: "Tell us about your plot, lifestyle and budget — our experts will draft a custom plan, BOQ and cost estimate within 5–7 working days.",
    },
    cart: {
      title: "Your Cart",
      empty: "Your cart is empty",
      emptySub: "Browse our plans and add the PDFs you need.",
      browse: "Browse plans",
      continueBrowse: "Continue browsing",
      itemSingular: "item", itemPlural: "items",
      pdfWatermarked: "PDF Watermarked",
      summary: "Order Summary",
      subtotal: "Subtotal",
      gst: "GST",
      total: "Total",
      proceed: "Proceed to Checkout",
      gstNote: "Final GST + buyer details collected on the next step.",
    },
    checkout: {
      backToCart: "Back to cart",
      title: "Checkout",
      buyerType: "Buyer Type",
      individual: "Individual", business: "Business",
      businessName: "Business Name", gstin: "GSTIN",
      gstinPh: "22AAAAA0000A1Z5",
      billingAddress: "Billing Address",
      fullName: "Full Name", mobile: "Mobile", email: "Email",
      address: "Address", city: "City", state: "State", pincode: "Pincode",
      selectState: "Select state",
      processing: "Processing…",
      pay: "Pay",
      paymentNote: "Secure payment via Razorpay • UPI / Card / Netbanking",
      summary: "Order Summary", totalPayable: "Total Payable",
      errName: "Name is required",
      errMobile: "Valid 10-digit mobile is required",
      errAddress: "Address is required",
      errCity: "City is required",
      errState: "State is required",
      errPincode: "Valid 6-digit pincode is required",
      errBusinessName: "Business name is required",
      errGstin: "GSTIN format is invalid (15 chars: 22AAAAA0000A1Z5)",
      errSdk: "Razorpay SDK not loaded. Please refresh.",
      errCancelled: "Payment cancelled",
      errFailed: "Oh no! Seems like payment failed. Please try again.",
    },
    orderSuccess: {
      title: "Payment successful",
      orderId: "Order ID",
      ready: "Your PDFs are ready to download.",
      loginToDownload: "Login to download your purchased files.",
      login: "Login",
      download: "Download",
      continueBrowse: "Continue browsing →",
    },
    blog: {
      title: "NakshaCart Blog",
      sub: "Guides on plot selection, BOQ, Vastu and home construction.",
      allArticles: "All articles",
    },
    careers: {
      title: "Careers at NakshaCart",
      sub: "Help us build the future of housing in India. Open roles across design, engineering and operations.",
      empty: "No openings right now — check back soon.",
      department: "Department",
      location: "Location",
      type: "Employment Type",
      posted: "Posted",
      backToCareers: "All openings",
      jobDescription: "Job Description",
      applyTitle: "Apply for this role",
      applyVia: "Apply by emailing your CV to",
      noEmail: "Reach out via our contact page to apply.",
      contactCta: "Go to contact",
      readMore: "View role",
      applyIntro: "Fill in the form below and upload your resume. Our HR team will review your application and get back to you.",
      fFullName: "Full name",
      fEmail: "Email",
      fMobile: "Mobile / WhatsApp",
      fLocation: "Current city",
      fExperience: "Years of experience",
      fCurrentRole: "Current role / employer",
      fCoverLetter: "Why do you want to join NakshaCart? (optional)",
      fResume: "Upload resume (PDF / DOC / DOCX, max 8 MB)",
      fResumeChoose: "Choose file",
      fResumeNone: "No file selected",
      submitApply: "Submit application",
      submitting: "Submitting…",
      applySuccess: "Application received! Our HR team will reach out shortly.",
      applyAnother: "Submit another application",
      requiredField: "Required",
      orOpenApp: "Don't see a role that fits?",
      openAppCta: "Submit an open application",
      openAppTitle: "Open Application",
      openAppSub: "Tell us about yourself and we'll keep your profile on file for matching roles.",
    },
    homeExtra: {
      featuredQuote: "\"Got my plan, BOQ and cost in one go. Saved weeks.\"",
      featuredQuoteAuthor: "— Rakesh, Patna",
      stat1: "House plans", stat2: "Verified partners", stat3: "Happy customers",
      benefit1: "House 2D Plan, 3D Model, Structural design, Plumbing & electrical drawings",
      benefit2: "BOQ + Cost Estimate included",
      benefit3: "Connect with verified contractors near you",
      stateLink: "Plans for {state}",
      partnerTag1: "Architects", partnerTag2: "Civil Engineers", partnerTag3: "Contractors",
      partnerTag4: "Electricians", partnerTag5: "Plumbers", partnerTag6: "Carpenters",
      partnerTag7: "Painters", partnerTag8: "Masons",
      getQuote: "Get a Quote",
      registerPartner: "Register as a partner",
      tm1Name: "Rakesh Kumar", tm1City: "Patna, Bihar", tm1Q: "Got my 30x40 plan in 2 minutes. The BOQ saved me 3 weeks of contractor back-and-forth.",
      tm2Name: "Priya Sharma", tm2City: "Lucknow, UP", tm2Q: "Loved that I could see the cost estimate upfront. NakshaCart's expert helped me hire a verified contractor.",
      tm3Name: "Anil Patil", tm3City: "Pune, Maharashtra", tm3Q: "Vastu-compliant plan + sample deliverables made it so easy. Highly recommended.",
      faq1Q: "How Does NakshaCart Work?",
      faq1A: "NakshaCart is committed to providing expert guidance throughout your entire building construction journey. This journey begins when you submit your plot details on our portal.\n\nOnce we receive your details, we provide two pre-designed house plans absolutely free, helping you understand, visualize, and finalize your dream home on paper before construction begins.\n\nThese plans are meant to give you a clear starting point for your project. However, if you are not an engineer or architect, we strongly advise not to begin construction based only on these plans.\n\nPlease note that building a house requires much more than just a floor plan. Structural design, elevation, electrical layout, plumbing, approvals, soil conditions, budgeting, and site supervision are also essential parts of successful construction.\n\nWhen you consult with NakshaCart, we provide complete professional support and the best possible services to help turn your dream home into reality.",
      faq2Q: "Are These Plans Vastu Compliant?",
      faq2A: "Vastu is a traditional concept of designing a house to create a positive and harmonious living environment.\n\nPlease note that achieving a 100% Vastu-compliant design is not always practically possible, especially on smaller plots where technical, functional, and space requirements must also be balanced.\n\nHowever, the basic layout and planning of our designs are created with Vastu principles in mind to ensure a positive and practical living space.\n\nIt is also important to understand that Vastu preferences can vary from person to person and from region to region. Because of this, we are always ready to provide unlimited modifications based on your specific Vastu requirements and personal preferences.",
      faq3Q: "Who builds the house after the design is finalized?",
      faq3A: "The construction can be handled in three ways: you may hire your own contractor, we can execute the project on a turnkey basis, or we can connect you with some of our verified partner contractors for the job. In all of the above cases, we are here to support you on call throughout the entire construction journey.",
      faq4Q: "What is the fee for your services?",
      faq4A: "Some of our fees are predefined, such as for downloading a plan or availing services listed on our pricing page. If your service requirements go beyond our predefined plans and offers, we will provide you with our best quotation after understanding your specific needs. Don't worry—we are up to 50% cheaper than the market while maintaining very high-quality services.",
    },
    about: {
      eyebrow: "About NakshaCart",
      title: "Building trust, plot by plot.",
      sub: "NakshaCart is a trusted platform operated by MECHACIV CONSTRUCTION PRIVATE LIMITED, delivering reliable construction, infrastructure, engineering, and design-related solutions across India.",
      intro: "We combine innovation, technology, and customer-focused services to simplify project planning and execution for individuals, businesses, and organizations. We believe in quality, transparency, and long-term relationships. Our mission is to provide dependable services while maintaining the highest standards of professionalism and customer satisfaction.",
      vision: "Our Vision",
      visionD: "To become a leading and trusted brand in construction and engineering support services by offering modern, efficient, and customer-centric solutions across India.",
      mission: "Our Mission",
      missionP1: "Deliver quality-driven services with transparency and integrity.",
      missionP2: "Build long-term customer relationships through trust and reliability.",
      missionP3: "Leverage technology and innovation for better project execution.",
      missionP4: "Provide professional support for residential, commercial, and infrastructure-related requirements.",
      companyInfo: "Company Information",
      brandName: "Brand Name",
      registeredCompany: "Registered Company",
      corporateOffice: "Corporate Office",
      address: "Address",
      corporateAddr: "UNIT NO B1211, 12TH FLOOR, TOWER T4, NX-1, Plot No 17 Tech Zone IV, Greater Noida (West), Uttar Pradesh",
      registeredOffice: "Registered Office",
      registeredAddr: "HN-502/3A, CN-50B, AT-JAKARIYAPUR, PO-BARI PAHARI, 2ND STREET EAST OF BAJRANGI CHOWK, PATNA-800007",
      branchOffice: "Branch Office",
      branchAddr: "226, Regus, Second Floor, Block-B, Shree Ram Business Park, Vidhan Sabha Road, Raipur",
      whyChoose: "Why Choose NakshaCart?",
      whyP1: "Professional and customer-focused approach",
      whyP2: "Reliable support and transparent communication",
      whyP3: "Experienced team and modern operational methods",
      whyP4: "Commitment to quality and timely service",
      whyP5: "Expanding presence across multiple locations in India",
      commitment: "Our Commitment",
      commitmentD: "At NakshaCart, we continuously strive to maintain excellence in every aspect of our operations. Whether serving individuals, businesses, or institutions, our goal is to provide dependable and efficient solutions that create long-term value for our customers. We thank our customers and partners for their continued trust and support.",
      contactUs: "Contact Us",
      contactUsD: "For business inquiries, partnerships, or support, please reach out through our official communication channels. We usually respond within 24 hours.",
      contactBtn: "Contact NakshaCart",
      talkBtn: "Talk to an expert",
      brandFooter: "NakshaCart — A Brand of",
      directorH: "Meet Our Director",
      directorName: "Anil Kumar Sahani",
      directorRole: "Director, NakshaCart",
      directorBio: "Anil Kumar Sahani is the Director of NakshaCart, with over 18 years of experience in engineering, construction management, and large-scale project execution across India and international markets. He has successfully worked on projects for reputed global clients such as Saudi Aramco, Reliance Industries, and Hitachi Germany.",
      directorIntl: "He also brings valuable international experience, having worked in the Kingdom of Saudi Arabia on complex industrial, fabrication, and infrastructure projects. His global exposure gives NakshaCart a professional edge with modern standards, efficient execution, and quality-driven practices.",
      directorVisionH: "His Vision",
      directorVisionD: "He founded NakshaCart to solve a common market problem — many buildings are constructed without expert supervision, leading to poor functionality, safety risks, and unnecessary costs due to weak planning and material wastage.",
      directorVisionD2: "His aim is to provide world-class services at street market prices, making professional building solutions affordable for everyone.",
      directorTrustH: "Why Trust Him",
      directorTrustIntro: "Customers trust Anil Kumar Sahani for his:",
      directorT1: "Strong professional and international experience",
      directorT2: "Proven industry leadership",
      directorT3: "Deep technical knowledge",
      directorT4: "Honest and transparent approach",
      directorT5: "Focus on safety, quality, and cost savings",
      directorMsgH: "Director's Message",
      directorMsg: "NakshaCart aims to make every project safe, functional, beautiful, and economical through expert guidance and trusted service.",
    },
    build: {
      chip: "End-to-end home construction",
      heroTitleA: "Build your dream home",
      heroTitleB: "on a fixed budget",
      heroSub: "Plan, design, sanction, build, hand over — all on a single fixed-price contract. 10-year structural warranty. 470+ quality checkpoints. No surprise escalations.",
      ctaCalc: "Get instant cost estimate",
      ctaTalk: "Talk to a build advisor",
      stat1: "Homes delivered", stat2: "Quality checkpoints",
      stat3: "Structural warranty", stat4: "On-time handover",
      warranty: "10-yr warranty", warrantySub: "Structural cover",
      heroQuote: "\"Fixed price held to the rupee. Two weeks ahead of schedule.\"",
      heroQuoteAuthor: "Arvind S. · Coimbatore",
      whyEyebrow: "Why NakshaCart Build",
      whyTitle: "The construction process, finally on your side",
      whySub: "Most owner-driven builds run 20% over budget and 3 months late. We replace that with paperwork, accountability, and a single point of contact.",
      vp1Title: "10-year structural warranty",
      vp1Body: "Every home we build is backed by a written warranty on the RCC frame and waterproofing.",
      vp2Title: "Fixed-price contract",
      vp2Body: "No surprise escalations. The number you sign for is the number you pay — locked at BOQ stage.",
      vp3Title: "470+ quality checkpoints",
      vp3Body: "From soil testing to final paint, every stage is inspected against a documented checklist.",
      vp4Title: "Vetted contractor network",
      vp4Body: "Only contractors with prior delivered projects and verified licenses are matched to your build.",
      pkgEyebrow: "Construction packages",
      pkgTitle: "Three packages. One fixed price.",
      pkgSub: "All-inclusive per-sqft rates. Pick the spec, we lock the number, no surprises.",
      pkgEstimate: "Estimate your home",
      pkgPerSqft: "/sqft",
      pkgRateNote: "All-inclusive · Tier-1 city base rate",
      pkgEstimateWith: "Estimate with {name}",
      classicName: "Classic", premiumName: "Premium", luxuryName: "Luxury",
      classicTag: "Smart starter", premiumTag: "Most chosen", luxuryTag: "Top-tier",
      classicHeadline: "Solid build, sensible spec",
      premiumHeadline: "Best balance of cost & finish",
      luxuryHeadline: "Imported, automated, immaculate",
      classicI1: "RCC frame structure (M25 / Fe 500)",
      classicI2: "Branded vitrified flooring (Kajaria / Somany)",
      classicI3: "Standard CP & sanitaryware (Cera)",
      classicI4: "Acrylic emulsion interior paint",
      classicI5: "Single-pole modular switches",
      premiumI1: "Premium fittings (Jaquar / Hindware)",
      premiumI2: "Designer wall textures in living + master",
      premiumI3: "Modular kitchen with soft-close drawers",
      premiumI4: "Smart electrical points (USB + AC pre-wire)",
      premiumI5: "Anti-skid premium tiles in wet areas",
      luxuryI1: "Imported Italian marble in living + dining",
      luxuryI2: "Premium German fittings (Grohe / Kohler)",
      luxuryI3: "Home automation: lights, locks, cameras",
      luxuryI4: "Designer false ceiling with cove lighting",
      luxuryI5: "VRV/VRF AC pre-installation",
      processEyebrow: "Our process",
      processTitle: "From plot to keys in four stages",
      p1Title: "Plan & quote", p1Body: "Share your plot details, pick a package, and get a fixed-price quote with full BOQ in 48 hours.", p1Days: "Day 1 – 7",
      p2Title: "Design & approvals", p2Body: "Our architects finalise drawings — vastu, structure, MEP — and handle local sanctions on your behalf.", p2Days: "Week 2 – 6",
      p3Title: "Construction", p3Body: "Daily site updates, weekly photo reports, and milestone-based payments. You see every rupee at work.", p3Days: "Month 2 – 8",
      p4Title: "Handover & warranty", p4Body: "Snag list cleared, completion certificate issued, and you walk in with 10-year structural cover.", p4Days: "Month 9",
      calcEyebrow: "Cost estimator",
      calcTitle: "Find out what your home will cost — in 60 seconds",
      calcSub: "Pick your city, enter plot dimensions, choose a package — get a transparent breakdown with structure, finishing, and MEP costs split out. No login required.",
      calcOpen: "Open the calculator",
      calcMeta: "Free · No login · Instant breakdown",
      tEyebrow: "From homeowners",
      tTitle: "Built. Delivered. On budget.",
      tm1Name: "Rajesh & Meena Kulkarni", tm1City: "Pune · 30×40 G+1", tm1Quote: "We were quoted 18% more by two local contractors. NakshaCart's fixed-price contract held to the rupee — and the weekly site reports kept my anxiety in check.",
      tm2Name: "Arvind Subramaniam", tm2City: "Coimbatore · 40×60 G+2", tm2Quote: "Picked the Premium package. The team handled DTCP approvals end-to-end which I'd been dreading. Moved in two weeks ahead of the promised date.",
      tm3Name: "Priya & Sandeep Bhatia", tm3City: "Gurgaon · 50×80 Villa", tm3Quote: "The architect actually listened. We changed the master suite layout twice in design phase — zero pushback, zero hidden charges. Final build is exactly the renderings.",
      ctaTitle1: "Already have a plot?",
      ctaTitle2: "Let's price your build today.",
      ctaSub: "Tell us a few things about your plot and we'll send back a no-obligation BOQ within 48 hours, plus a build advisor who'll walk you through the numbers line by line.",
      ctaP1: "Free site visit included",
      ctaP2: "No commitment until you sign the contract",
      ctaP3: "Compare against any other quote, line by line",
      faqEyebrow: "Frequently asked",
      faqTitle: "The questions everyone asks",
      faq1Q: "How is the cost fixed if material rates fluctuate?",
      faq1A: "Our BOQ locks per-sqft rates by package and scope at contract signing. Cement, steel, and labour cost variances are absorbed by us — that's the point of a fixed-price contract. The only line items that can change are owner-driven scope additions, and you approve each one in writing.",
      faq2Q: "What's actually included in the per-sqft rate?",
      faq2A: "Everything from the foundation up to a livable home: structure, masonry, plastering, plumbing & electrical, flooring, doors & windows, kitchen counter, bathroom fittings, paint, and a basic compound wall. Add-ons like lifts, solar, modular kitchen upgrades, and landscaping are separate and listed transparently in the BOQ.",
      faq3Q: "Do you handle approvals and sanctions?",
      faq3A: "Not directly, but for building plan sanction, BWSSB/BESCOM connections, soil testing reports, and completion certificates, we support you by providing the necessary drawings and documentation required for approvals. Final approvals must be obtained by you as per local body norms.",
      faq4Q: "How long does a typical 2,500 sqft G+1 home take?",
      faq4A: "Roughly 7–9 months from foundation pour to handover, plus 4–6 weeks for design and approvals before that. Monsoon delays are factored into the contract timeline upfront.",
      faq5Q: "What happens if I want to make changes mid-build?",
      faq5A: "Up to 5% scope change is included free. Beyond that, you sign a written change order with the cost delta before work proceeds. No surprise invoices, ever.",
      leadMessageLabel: "Is there anything else you'd like to tell us?",
      leadSubmit: "SUBMIT",
    },
    pricingPage: {
      title: "Transparent pricing for design & construction",
      sub: "Pick what you need — a design package or a full construction estimate. No hidden charges, no recurring fees.",
      tabDesign: "Design", tabDesignSub: "Drawings & plans",
      tabConstruction: "Construction", tabConstructionSub: "Build cost estimator",
      sectionTitle: "Design Plans",
      sectionSub: "One-time price. Pick the tier that matches the depth of design you need — from a simple Naksha to a fully structural set with site support.",
      customisedForPlot: "CUSTOMISED FOR YOUR PLOT",
      specialOffer: "Special G+2 Home Design Offer for Small Land Owners – Up to 1500 Sq.ft.",
      paymentNote: "Simply choose the package that best suits your requirements. Once your payment is completed, our team will contact you to discuss your project needs in detail. Don't worry — your money is completely safe. We offer a 100% refundable policy if you request cancellation before the design work has started.",
      compare: "Compare",
      popular: "POPULAR", mostPopular: "MOST POPULAR",
      save: "Save {pct}%", off: "{pct}% OFF",
      included: "Included",
      getStarted: "Get started",
      tierBasic: "Basic", tierStandard: "Standard", tierPopular: "Popular", tierPremium: "Premium",
      focusNaksha: "Naksha", focusArchitectural: "Architectural", focusConstruction: "Construction", focusStructural: "Structural",
      validity30: "30 Days", validity90: "90 Days", validity365: "365 Days",
      f2d: "2D Plan", fElev: "Elevation", f3d: "3D Model",
      fPlumb: "Plumbing Drawing", fElec: "Electrical Drawing",
      fStruct: "Structural Drawing", fSupport: "On-Call Site Support",
      fValidity: "Modification Validity",
      footnote: "Prices are one-time. Modification validity is the window during which we revise the drawings free of cost based on your feedback. After expiry, modifications are charged at standard hourly rates.",
      notSure: "Not sure which tier fits?",
      notSureSub: "Tell us your plot dimensions and budget. Our design advisor will recommend the right package and walk you through what's inside.",
      talkAdvisor: "Talk to an advisor",
      browseReady: "Browse ready plans",
    },
    services: {
      eyebrow: "Our Services",
      title: "Everything you need to build your home",
      sub: "From the first drawing to the final key — NakshaCart's experts handle every stage of your construction journey.",
      s1Title: "Architectural Design", s1Sub: "Home Plan, Elevation & 3D Model",
      s1Desc: "We prepare a complete architectural package for your plot — 2D floor plan, all elevations (front, rear, side), and a detailed 3D model so you can visualise your dream home before a single brick is laid.",
      s1P1: "2D Floor Plan (all floors)", s1P2: "Front, Rear & Side Elevations", s1P3: "3D Model / Walkthrough", s1P4: "Vastu-compliant layout",
      s2Title: "Structural Design", s2Sub: "RCC & Foundation Drawings",
      s2Desc: "Our structural engineers prepare a full structural drawing set based on your architectural plan — ensuring the building is safe, code-compliant, and optimised for your local soil conditions.",
      s2P1: "Column & beam layout", s2P2: "Slab & foundation design", s2P3: "RCC bar-bending schedule", s2P4: "Soil & load analysis",
      s3Title: "Plumbing Drawing", s3Sub: "Water Supply & Drainage Layout",
      s3Desc: "Detailed plumbing drawings covering all water-supply lines, drainage routing, fixture positions, and pipe sizing — handed over in a format your plumber can directly execute from.",
      s3P1: "Water supply layout", s3P2: "Drainage & sewage routing", s3P3: "Fixture & tap positions", s3P4: "Pipe sizing & material spec",
      s4Title: "Electrical Drawing", s4Sub: "Wiring, Load & Switch Layout",
      s4Desc: "Complete electrical layout drawings for your home — point-by-point wiring plan, load calculations, switchboard positions, earthing, and compliance with IS standards.",
      s4P1: "Point-by-point wiring plan", s4P2: "Load calculation sheet", s4P3: "Switchboard & DB layout", s4P4: "Earthing & safety compliance",
      s5Title: "Construction Supervision", s5Sub: "Site Visits & Quality Checks",
      s5Desc: "Our empanelled civil engineers visit your site at critical construction stages — from foundation to slab to finishes — ensuring quality, catching errors early, and keeping your contractor accountable.",
      s5P1: "Stage-wise site visits", s5P2: "Quality & material checks", s5P3: "Progress reporting", s5P4: "Contractor coordination",
      s6Title: "Turnkey Construction", s6Sub: "End-to-End Build & Material Support",
      s6Desc: "Hand over your plot and walk into your finished home. We manage design, procurement, construction and finishing — sourcing materials at the best rates and delivering a complete, ready-to-move house.",
      s6P1: "End-to-end project management", s6P2: "Material procurement support", s6P3: "Transparent BOQ & billing", s6P4: "Fixed timeline commitment",
      ctaChip: "Get started",
      ctaTitle: "Tell us what you need",
      ctaSub: "Share your plot details and the service you're looking for. Our experts will get back to you with a timeline and quote.",
      ctaP1: "Free initial consultation",
      ctaP2: "Transparent, competitive pricing",
      ctaP3: "Delivery commitment in writing",
    },
  },
  hi: {
    nav: {
      home: "होम", plans: "नक्शा (हाउस प्लान)", pricing: "मूल्य", customize: "कस्टमाइज़",
      blog: "ब्लॉग", contact: "संपर्क", login: "लॉगिन", signup: "साइन अप",
      dashboard: "डैशबोर्ड", logout: "लॉगआउट", partnerWith: "पार्टनर बनें",
      services: "सेवाएँ", becomePartner: "पार्टनर बनें",
      more: "और देखें", build: "घर बनाएँ", about: "हमारे बारे में",
    },
    hero: {
      tagline: "तुरंत प्लान • BOQ • लागत • वेरिफाइड पार्टनर",
      h1a: "सिर्फ़ 2 मिनट में",
      h1b: "अपना घर का नक्शा पाएं",
      sub: "प्लॉट का माप दीजिए — प्लान, BOQ और लागत का अनुमान तुरंत पाइए। साथ ही सही तरीक़े से बनवाने के लिए विशेषज्ञ सलाह।",
      length: "लंबाई (फीट)", width: "चौड़ाई (फीट)", floors: "मंज़िलें",
      facing: "मुख्य दिशा", findPlan: "मेरा प्लान ढूँढें",
      vastuToggle: "NakshaCart के सभी प्लान वास्तु अनुसार डिज़ाइन किए जाते हैं।",
      facingLabel: "मुख्य दिशा",
      surroundingsToggle: "आपके प्लॉट के किनारे क्या है?",
      surroundingsOptional: "(वैकल्पिक)",
      surroundingsHelp: "इससे हम वास्तु के अनुकूल लेआउट सुझा पाते हैं। यह जानकारी आपकी पूछताछ के साथ सुरक्षित कर ली जाएगी।",
      chip1: "2 मुफ़्त बंडल", chip2: "BOQ शामिल", chip3: "EN + हिंदी",
    },
    facing: { N: "उत्तर", S: "दक्षिण", E: "पूर्व", W: "पश्चिम", NE: "उत्तर-पूर्व", NW: "उत्तर-पश्चिम", SE: "दक्षिण-पूर्व", SW: "दक्षिण-पश्चिम", ANY: "कोई भी" },
    cta: { talkExpert: "विशेषज्ञ से बात करें", getPlan: "प्लान पाएँ", customize: "इस प्लान को कस्टमाइज़ करें", needHelp: "कस्टम डिज़ाइन चाहिए?" },
    sections: {
      featured: "फ़ीचर्ड प्लान", featuredSub: "देश भर के बिल्डरों द्वारा पसंद किए गए प्लान।",
      categories: "श्रेणी से देखें", categoriesSub: "अपने प्लॉट और जीवनशैली के अनुकूल विकल्प चुनें।",
      how: "NakshaCart कैसे काम करता है", howSub: "प्लॉट से प्लान, और प्लान से पार्टनर तक — सिर्फ़ 4 चरण।",
      stats: "देश भर के बिल्डरों का भरोसा",
      whyUs: "क्यों NakshaCart", whyUsSub: "जैसे भारतीय असल में घर बनाते हैं — उसी के लिए बनाया गया।",
      states: "अपने राज्य के प्लान खोजें",
      testimonials: "ग्राहकों की कहानियाँ",
      partners: "हमारे वेरिफाइड पार्टनर", partnersSub: "आर्किटेक्ट, सिविल इंजीनियर और कॉन्ट्रैक्टर — हमारे द्वारा चयनित।",
      faq: "अक्सर पूछे जाने वाले प्रश्न",
      leadTitle: "सही प्लान नहीं मिला?",
      leadSub: "हमारे विशेषज्ञ कस्टमाइज़्ड समाधानों के लिए बहुत कम शुल्क लेंगे।",
      featuredAction: "सभी देखें",
      asFeaturedIn: "जिनमें फ़ीचर हुए",
    },
    how: {
      s1: "प्लॉट दर्ज करें", s1d: "लंबाई, चौड़ाई, मंज़िलें, दिशा — बस इतना।",
      s2: "मैच हुए प्लान देखें", s2d: "हम वेरिफाइड लाइब्रेरी से सबसे क़रीब के प्लान दिखाते हैं।",
      s3: "बंडल मुफ़्त डाउनलोड करें", s3d: "फ्लोर प्लान + BOQ + लागत अनुमान।",
      s4: "विशेषज्ञ से जुड़ें", s4d: "और अपनी ज़रूरतें साझा करें — हम मिलकर आपके सपने को साकार करेंगे।",
      step: "चरण",
    },
    why: {
      one: "तुरंत प्लान", oneD: "परिवार के साथ चर्चा और ज़रूरत व अवधारणा को अंतिम रूप देने के लिए।",
      two: "प्लान फाइनलाइज़ेशन", twoD: "चर्चा के बाद हम आपके घर का प्लान तैयार करते हैं। आप अपनी अवधारणा के अनुसार प्लान में असीमित बदलाव कर सकते हैं।",
      three: "BOQ + लागत पारदर्शिता", threeD: "फाइनल प्लान के आधार पर BOQ और लागत अनुमान भी दिया जाएगा। 3D मॉडल और अन्य ड्रॉइंग भी तैयार की जाएंगी।",
    },
    plan: {
      bhk: "BHK", floors: "मंज़िलें", area: "बिल्ट-अप", facing: "दिशा",
      view: "विवरण देखें", download: "डाउनलोड",
      floorPlan: "फ्लोर प्लान", boq: "BOQ", cost: "लागत अनुमान", cad: "ऑटोकैड ड्रॉइंग",
      locked: "अनलॉक करने के लिए अपग्रेड करें", premium: "प्रीमियम",
    },
    leadForm: {
      name: "आपका नाम", mobile: "मोबाइल नंबर", email: "ईमेल (वैकल्पिक)",
      message: "अपने प्लॉट या प्रोजेक्ट के बारे में बताएँ",
      submit: "मुझे कॉल करें", success: "धन्यवाद! हमारी टीम 24 घंटे में संपर्क करेगी।",
      thankYou: "धन्यवाद",
      plotLength: "प्लॉट लंबाई (फीट)", plotWidth: "प्लॉट चौड़ाई (फीट)",
      plotLengthPh: "जैसे 30", plotWidthPh: "जैसे 40",
      facingLabel: "रोड फेसिंग दिशा", selectFacing: "दिशा चुनें",
      surroundings: "आपके प्लॉट के हर किनारे पर क्या है?",
      state: "राज्य", district: "ज़िला",
      statePh: "महाराष्ट्र", districtPh: "पुणे",
      disclaimer: "सबमिट करके आप NakshaCart पार्टनर्स से संपर्क पाने के लिए सहमति देते हैं। हम आपकी जानकारी कभी नहीं बेचते।",
      errRequired: "नाम और मोबाइल आवश्यक हैं",
    },
    compass: {
      select: "चुनें…", yourPlot: "आपका प्लॉट",
      side: { road: "सड़क", anotherPlot: "दूसरा प्लॉट", openLand: "खाली ज़मीन", pond: "तालाब / जलस्रोत", nala: "नाला / नाली", building: "इमारत", garden: "बग़ीचा / खेत", other: "अन्य" },
    },
    footer: {
      tagline: "2 मिनट में प्लान। वेरिफाइड पार्टनर के साथ सही तरीक़े से बना।",
      product: "प्रोडक्ट", company: "कंपनी", legal: "नियम",
      about: "हमारे बारे में", services: "सेवाएँ", terms: "नियम और शर्तें", privacy: "गोपनीयता",
      copyright: "© 2026 NakshaCart. भारत में, भारत के लिए डिज़ाइन किया गया।",
      affiliate: "एफ़िलिएट प्रोग्राम",
      legalAll: "सभी क़ानूनी नीतियाँ",
      refund: "रिफंड नीति", cancellation: "रद्दीकरण नीति", delivery: "डिलिवरी नीति",
      adminLogin: "एडमिन लॉगिन",
      careers: "करियर",
    },
    auth: {
      mobile: "मोबाइल नंबर", password: "पासवर्ड", name: "पूरा नाम", email: "ईमेल (वैकल्पिक)",
      loginTitle: "वापसी पर स्वागत है", loginSub: "प्लान डाउनलोड और पार्टनर से जुड़ने के लिए लॉगिन करें।",
      signupTitle: "अपना अकाउंट बनाएँ", signupSub: "लाइफटाइम 2 मुफ़्त प्लान डाउनलोड।",
      signupCta: "अकाउंट बनाएँ", loginCta: "लॉगिन", noAccount: "अकाउंट नहीं है?", hasAccount: "पहले से अकाउंट है?",
      partnerCta: "पार्टनर बनें", forgotPassword: "पासवर्ड भूल गए?",
    },
    contact: {
      title: "NakshaCart से संपर्क करें",
      sub: "किसी प्लान, साझेदारी या कस्टम डिज़ाइन के बारे में सवाल हैं? हमें आपसे सुनकर ख़ुशी होगी।",
      phone: "फ़ोन", email: "ईमेल", hq: "मुख्यालय", hqValue: "बेंगलुरु, भारत",
    },
    plansPage: {
      title: "हाउस प्लान लाइब्रेरी",
      sub: "{n}+ वेरिफाइड प्लान देखें, या फ़िल्टर लगाकर छाँटें।",
      anyFacing: "कोई भी दिशा", anyFloors: "कोई भी मंज़िल", anyBhk: "कोई भी BHK",
      allCategories: "सभी श्रेणियाँ",
      catBudget: "बजट", catDuplex: "डुप्लेक्स", catLuxury: "लग्ज़री", catVastu: "वास्तु",
      clearAll: "सभी साफ़ करें",
      matchInfo: "{length}×{width} फीट, {floors} मंज़िल, {facing} दिशा के लिए {count} मैच हुए प्लान दिखाए जा रहे हैं।",
      noMatchTitle: "अपना सही प्लान नहीं मिला?",
      noMatchSub: "हमारे विशेषज्ञों से कस्टम डिज़ाइन पाएँ — आपके प्लॉट के लिए 5–7 कार्य दिवसों में तैयार।",
      floorsOpt: "{n} मंज़िलें", bhkOpt: "{n} BHK",
    },
    planDetail: {
      backToPlans: "प्लान पर वापस",
      clickToZoom: "ज़ूम के लिए क्लिक करें",
      productDescription: "उत्पाद विवरण",
      floorDescription: "फ्लोर विवरण",
      plotDetails: "प्लॉट विवरण",
      vastuCompliant: "वास्तु अनुकूल",
      estCost: "अनुमानित निर्माण लागत",
      customize: "इस प्लान को कस्टमाइज़ करें",
      downloadBundle: "बंडल डाउनलोड",
      bundleNote: "प्रति-PDF भुगतान • वॉटरमार्क • चेकआउट पर 18% GST",
      pdfWatermarked: "PDF • वॉटरमार्क",
      inCart: "कार्ट में", add: "जोड़ें",
      addToCart: "कार्ट में जोड़ें", buyNow: "अभी ख़रीदें",
      viewCart: "कार्ट देखें ({n}) →",
      specWidth: "चौड़ाई", specLength: "लंबाई / गहराई", specPlotArea: "प्लॉट क्षेत्र",
      specFloors: "मंज़िलें", specBuiltUp: "बिल्ट-अप क्षेत्र", specFacing: "दिशा",
      specCategory: "श्रेणी", specEstCost: "अनुमानित निर्माण लागत",
      roomBedrooms: "बेडरूम", roomLiving: "लिविंग रूम", roomBathroom: "बाथरूम", roomKitchen: "किचन",
    },
    planCard: {
      featured: "फ़ीचर्ड", from: "से", gst: "+ 18% GST",
    },
    pricing: {
      chip: "केवल उन्हीं PDF का भुगतान जो आपको चाहिए",
      title: "सरल, प्रति-PDF मूल्य",
      sub: "हर प्लान में Floor Plan, BOQ और लागत अनुमान के अलग-अलग दाम लिखे होते हैं। ज़रूरत की चीज़ें कार्ट में डालें, एक बार भुगतान करें, हमेशा डाउनलोड करें। सभी भुगतान वाली चीज़ों पर {gst}% GST लगता है।",
      free: "मुफ़्त",
      floorPlanLabel: "फ्लोर प्लान", floorPlanNote: "विस्तृत मापों वाला फ्लोर लेआउट — आपका मुख्य ब्लूप्रिंट।",
      boqLabel: "बिल ऑफ क्वांटिटीज़", boqNote: "ज़रूरी सामग्री की लाइन-दर-लाइन सूची — कॉन्ट्रैक्टर की पैडिंग को रोकती है।",
      costLabel: "लागत अनुमान", costNote: "निर्माण की अपेक्षित लागत आइटम और चरण के अनुसार।",
      freeAvailable: "कुछ प्लान में यह मुफ़्त शामिल है।",
      feature1: "वॉटरमार्क PDF तुरंत डिलीवर",
      feature2: "पारदर्शी {gst}% GST — चेकआउट पर जुड़ता है",
      feature3: "व्यवसायों के लिए GSTIN इनवॉइस",
      ctaTitle: "कस्टम डिज़ाइन या 2000 sqft से बड़े प्लॉट के लिए चाहिए?",
      ctaSub: "हमारे विशेषज्ञों से बात करें — पहली सलाह मुफ़्त है।",
      browse: "प्लान देखें", talk: "हमसे बात करें",
    },
    customize: {
      chip: "विशेषज्ञ",
      title: "आपके प्लॉट के लिए तैयार किया गया प्लान पाएँ",
      sub: "अपने प्लॉट, जीवनशैली और बजट के बारे में बताएँ — हमारे विशेषज्ञ 5–7 कार्य दिवसों में कस्टम प्लान, BOQ और लागत अनुमान तैयार कर देंगे।",
    },
    cart: {
      title: "आपका कार्ट",
      empty: "आपका कार्ट खाली है",
      emptySub: "हमारे प्लान देखें और ज़रूरी PDF जोड़ें।",
      browse: "प्लान देखें",
      continueBrowse: "देखना जारी रखें",
      itemSingular: "आइटम", itemPlural: "आइटम",
      pdfWatermarked: "PDF वॉटरमार्क",
      summary: "ऑर्डर सारांश",
      subtotal: "सबटोटल",
      gst: "GST",
      total: "कुल",
      proceed: "चेकआउट पर जाएँ",
      gstNote: "अंतिम GST + ख़रीदार विवरण अगले चरण में लिए जाएँगे।",
    },
    checkout: {
      backToCart: "कार्ट पर वापस",
      title: "चेकआउट",
      buyerType: "ख़रीदार प्रकार",
      individual: "व्यक्तिगत", business: "व्यवसाय",
      businessName: "व्यवसाय का नाम", gstin: "GSTIN",
      gstinPh: "22AAAAA0000A1Z5",
      billingAddress: "बिलिंग पता",
      fullName: "पूरा नाम", mobile: "मोबाइल", email: "ईमेल",
      address: "पता", city: "शहर", state: "राज्य", pincode: "पिनकोड",
      selectState: "राज्य चुनें",
      processing: "प्रोसेस हो रहा है…",
      pay: "भुगतान",
      paymentNote: "Razorpay के माध्यम से सुरक्षित भुगतान • UPI / कार्ड / नेटबैंकिंग",
      summary: "ऑर्डर सारांश", totalPayable: "कुल देय",
      errName: "नाम आवश्यक है",
      errMobile: "मान्य 10-अंकीय मोबाइल आवश्यक है",
      errAddress: "पता आवश्यक है",
      errCity: "शहर आवश्यक है",
      errState: "राज्य आवश्यक है",
      errPincode: "मान्य 6-अंकीय पिनकोड आवश्यक है",
      errBusinessName: "व्यवसाय का नाम आवश्यक है",
      errGstin: "GSTIN प्रारूप अमान्य है (15 अक्षर: 22AAAAA0000A1Z5)",
      errSdk: "Razorpay SDK लोड नहीं हुआ। पेज रीफ़्रेश करें।",
      errCancelled: "भुगतान रद्द किया गया",
      errFailed: "ओह! भुगतान विफल हो गया। कृपया पुनः प्रयास करें।",
    },
    orderSuccess: {
      title: "भुगतान सफल",
      orderId: "ऑर्डर ID",
      ready: "आपकी PDFs डाउनलोड के लिए तैयार हैं।",
      loginToDownload: "ख़रीदी गई फ़ाइलें डाउनलोड करने के लिए लॉगिन करें।",
      login: "लॉगिन",
      download: "डाउनलोड",
      continueBrowse: "देखना जारी रखें →",
    },
    blog: {
      title: "NakshaCart ब्लॉग",
      sub: "प्लॉट चयन, BOQ, वास्तु और घर निर्माण पर गाइड।",
      allArticles: "सभी लेख",
    },
    careers: {
      title: "NakshaCart में करियर",
      sub: "भारत के घर बनाने के भविष्य में हमारे साथ जुड़ें। डिज़ाइन, इंजीनियरिंग और ऑपरेशन्स में नई भूमिकाएँ खुली हैं।",
      empty: "अभी कोई पद उपलब्ध नहीं है — कृपया कुछ समय बाद देखें।",
      department: "विभाग",
      location: "स्थान",
      type: "नियुक्ति का प्रकार",
      posted: "पोस्ट किया गया",
      backToCareers: "सभी पद",
      jobDescription: "नौकरी का विवरण",
      applyTitle: "इस पद के लिए आवेदन करें",
      applyVia: "अपना CV ईमेल करके आवेदन करें —",
      noEmail: "आवेदन करने के लिए हमारे संपर्क पेज पर जाएँ।",
      contactCta: "संपर्क पर जाएँ",
      readMore: "पद देखें",
      applyIntro: "नीचे फ़ॉर्म भरें और अपना रिज़्यूमे अपलोड करें। हमारी HR टीम आपके आवेदन की समीक्षा करके आपसे संपर्क करेगी।",
      fFullName: "पूरा नाम",
      fEmail: "ईमेल",
      fMobile: "मोबाइल / WhatsApp",
      fLocation: "वर्तमान शहर",
      fExperience: "अनुभव (वर्ष)",
      fCurrentRole: "वर्तमान भूमिका / नियोक्ता",
      fCoverLetter: "आप NakshaCart से क्यों जुड़ना चाहते हैं? (वैकल्पिक)",
      fResume: "रिज़्यूमे अपलोड करें (PDF / DOC / DOCX, अधिकतम 8 MB)",
      fResumeChoose: "फ़ाइल चुनें",
      fResumeNone: "कोई फ़ाइल नहीं चुनी गई",
      submitApply: "आवेदन भेजें",
      submitting: "भेजा जा रहा है…",
      applySuccess: "आवेदन प्राप्त हो गया! हमारी HR टीम जल्द ही आपसे संपर्क करेगी।",
      applyAnother: "एक और आवेदन भेजें",
      requiredField: "ज़रूरी",
      orOpenApp: "आपके लिए उपयुक्त पद नहीं दिख रहा?",
      openAppCta: "एक ओपन आवेदन भेजें",
      openAppTitle: "ओपन आवेदन",
      openAppSub: "अपने बारे में बताएँ — हम आपके प्रोफ़ाइल को उपयुक्त पदों के लिए सहेज लेंगे।",
    },
    homeExtra: {
      featuredQuote: "\"एक ही बार में प्लान, BOQ और लागत मिल गए। हफ़्तों की बचत हुई।\"",
      featuredQuoteAuthor: "— राकेश, पटना",
      stat1: "हाउस प्लान", stat2: "वेरिफाइड पार्टनर", stat3: "ख़ुश ग्राहक",
      benefit1: "हाउस 2D प्लान, 3D मॉडल, स्ट्रक्चरल डिज़ाइन, प्लंबिंग व इलेक्ट्रिकल ड्रॉइंग",
      benefit2: "BOQ + लागत अनुमान शामिल",
      benefit3: "अपने पास के वेरिफाइड कॉन्ट्रैक्टर से जुड़ें",
      stateLink: "{state} के प्लान",
      partnerTag1: "आर्किटेक्ट", partnerTag2: "सिविल इंजीनियर", partnerTag3: "कॉन्ट्रैक्टर",
      partnerTag4: "इलेक्ट्रिशियन", partnerTag5: "प्लंबर", partnerTag6: "बढ़ई",
      partnerTag7: "पेंटर", partnerTag8: "राजमिस्त्री",
      getQuote: "कोटेशन पाएँ",
      registerPartner: "पार्टनर के रूप में रजिस्टर करें",
      tm1Name: "राकेश कुमार", tm1City: "पटना, बिहार", tm1Q: "अपना 30x40 प्लान 2 मिनट में मिल गया। BOQ ने कॉन्ट्रैक्टर से चर्चा के 3 हफ़्ते बचाए।",
      tm2Name: "प्रिया शर्मा", tm2City: "लखनऊ, UP", tm2Q: "लागत अनुमान पहले देख पाना अच्छा लगा। NakshaCart के विशेषज्ञ ने वेरिफाइड कॉन्ट्रैक्टर ढूँढने में मदद की।",
      tm3Name: "अनिल पाटिल", tm3City: "पुणे, महाराष्ट्र", tm3Q: "वास्तु-अनुकूल प्लान + सैंपल डिलिवरेबल ने सब कुछ आसान बना दिया। बहुत सिफ़ारिश करूँगा।",
      faq1Q: "NakshaCart कैसे काम करता है?",
      faq1A: "NakshaCart आपकी पूरी निर्माण यात्रा में विशेषज्ञ मार्गदर्शन देने के लिए प्रतिबद्ध है। यह यात्रा तब शुरू होती है जब आप हमारे पोर्टल पर अपने प्लॉट का विवरण भरते हैं।\n\nविवरण मिलने के बाद हम बिल्कुल मुफ़्त दो पूर्व-डिज़ाइन किए हाउस प्लान देते हैं ताकि आप निर्माण शुरू होने से पहले अपने सपनों के घर को कागज़ पर समझ, देख और अंतिम रूप दे सकें।\n\nये प्लान आपके प्रोजेक्ट के लिए स्पष्ट शुरुआती बिंदु देने के लिए हैं। हालाँकि, यदि आप इंजीनियर या आर्किटेक्ट नहीं हैं, तो हम सलाह देते हैं कि केवल इन्हीं प्लान के आधार पर निर्माण न शुरू करें।\n\nकृपया ध्यान दें कि एक घर बनाने के लिए सिर्फ़ फ्लोर प्लान से कहीं ज़्यादा चाहिए। स्ट्रक्चरल डिज़ाइन, एलिवेशन, इलेक्ट्रिकल लेआउट, प्लंबिंग, अनुमतियाँ, मिट्टी की स्थिति, बजट और साइट सुपरविज़न भी ज़रूरी हैं।\n\nजब आप NakshaCart से सलाह लेते हैं, हम पूरी प्रोफ़ेशनल सहायता और बेहतरीन सेवाएँ देते हैं ताकि आपके सपनों का घर हक़ीक़त बन सके।",
      faq2Q: "क्या ये प्लान वास्तु अनुकूल हैं?",
      faq2A: "वास्तु एक पारंपरिक अवधारणा है जो सकारात्मक और सामंजस्यपूर्ण निवास के लिए घर डिज़ाइन करने का तरीक़ा बताती है।\n\nकृपया ध्यान दें कि 100% वास्तु-अनुकूल डिज़ाइन हर बार व्यावहारिक रूप से संभव नहीं होता, ख़ासकर छोटे प्लॉट पर जहाँ तकनीकी, कार्यात्मक और स्थान संबंधी ज़रूरतें भी संतुलित रखनी होती हैं।\n\nफिर भी हमारे डिज़ाइन का बुनियादी लेआउट और प्लानिंग वास्तु सिद्धांतों को ध्यान में रखकर बनाया जाता है ताकि सकारात्मक और व्यावहारिक रहन-सहन का स्थान मिले।\n\nयह भी समझना ज़रूरी है कि वास्तु प्राथमिकताएँ व्यक्ति-दर-व्यक्ति और क्षेत्र-दर-क्षेत्र अलग होती हैं। इसी कारण हम आपकी विशिष्ट वास्तु आवश्यकताओं के अनुसार असीमित संशोधन देने को हमेशा तैयार हैं।",
      faq3Q: "डिज़ाइन तय होने के बाद घर कौन बनाता है?",
      faq3A: "निर्माण तीन तरीक़ों से किया जा सकता है: आप अपना कॉन्ट्रैक्टर रख सकते हैं, हम टर्नकी आधार पर पूरा प्रोजेक्ट चला सकते हैं, या हम आपको अपने वेरिफाइड पार्टनर कॉन्ट्रैक्टर्स से जोड़ सकते हैं। तीनों स्थितियों में हम पूरी निर्माण यात्रा में फ़ोन पर सहायता के लिए मौजूद रहते हैं।",
      faq4Q: "आपकी सेवाओं का शुल्क क्या है?",
      faq4A: "हमारी कुछ फ़ीस पहले से तय हैं — जैसे प्लान डाउनलोड या मूल्य पेज पर सूचीबद्ध सेवाएँ। यदि आपकी ज़रूरतें इनसे आगे जाती हैं तो हम आपकी आवश्यकता समझकर सर्वोत्तम कोटेशन देंगे। चिंता न करें — बहुत उच्च गुणवत्ता बनाए रखते हुए हम बाज़ार से 50% तक सस्ते हैं।",
    },
    about: {
      eyebrow: "NakshaCart के बारे में",
      title: "हर प्लॉट पर भरोसा बनाते हुए।",
      sub: "NakshaCart एक भरोसेमंद प्लेटफ़ॉर्म है जिसे MECHACIV CONSTRUCTION PRIVATE LIMITED द्वारा संचालित किया जाता है — पूरे भारत में निर्माण, इन्फ़्रास्ट्रक्चर, इंजीनियरिंग और डिज़ाइन से जुड़ी विश्वसनीय सेवाएँ देने वाला।",
      intro: "हम नवाचार, तकनीक और ग्राहक-केंद्रित सेवाओं को मिलाकर व्यक्तियों, व्यवसायों और संस्थानों के लिए परियोजना नियोजन और निष्पादन को सरल बनाते हैं। हम गुणवत्ता, पारदर्शिता और दीर्घकालिक संबंधों में विश्वास रखते हैं। हमारा मिशन है — पेशेवर मानकों और ग्राहक संतुष्टि के सर्वोच्च स्तर के साथ भरोसेमंद सेवाएँ देना।",
      vision: "हमारी दृष्टि",
      visionD: "पूरे भारत में आधुनिक, कुशल और ग्राहक-केंद्रित समाधान देकर निर्माण और इंजीनियरिंग सहायता सेवाओं में अग्रणी और भरोसेमंद ब्रांड बनना।",
      mission: "हमारा मिशन",
      missionP1: "पारदर्शिता और सत्यनिष्ठा के साथ गुणवत्ता-संचालित सेवाएँ देना।",
      missionP2: "भरोसे और विश्वसनीयता के माध्यम से दीर्घकालिक ग्राहक संबंध बनाना।",
      missionP3: "बेहतर परियोजना निष्पादन के लिए तकनीक और नवाचार का उपयोग।",
      missionP4: "आवासीय, व्यावसायिक और इन्फ़्रास्ट्रक्चर ज़रूरतों के लिए पेशेवर सहायता प्रदान करना।",
      companyInfo: "कंपनी जानकारी",
      brandName: "ब्रांड नाम",
      registeredCompany: "रजिस्टर्ड कंपनी",
      corporateOffice: "कॉर्पोरेट कार्यालय",
      address: "पता",
      corporateAddr: "UNIT NO B1211, 12वीं मंज़िल, टॉवर T4, NX-1, प्लॉट नंबर 17 टेक ज़ोन IV, ग्रेटर नोएडा (पश्चिम), उत्तर प्रदेश",
      registeredOffice: "रजिस्टर्ड कार्यालय",
      registeredAddr: "HN-502/3A, CN-50B, AT-जकरियापुर, PO-बारी पहाड़ी, बजरंगी चौक के पूर्व में दूसरी गली, पटना-800007",
      branchOffice: "शाखा कार्यालय",
      branchAddr: "226, Regus, दूसरी मंज़िल, ब्लॉक-B, श्री राम बिज़नेस पार्क, विधान सभा रोड, रायपुर",
      whyChoose: "NakshaCart क्यों चुनें?",
      whyP1: "पेशेवर और ग्राहक-केंद्रित दृष्टिकोण",
      whyP2: "भरोसेमंद सहायता और पारदर्शी संवाद",
      whyP3: "अनुभवी टीम और आधुनिक कार्यप्रणाली",
      whyP4: "गुणवत्ता और समय पर सेवा की प्रतिबद्धता",
      whyP5: "पूरे भारत में बढ़ती मौजूदगी",
      commitment: "हमारी प्रतिबद्धता",
      commitmentD: "NakshaCart पर हम अपने हर काम में उत्कृष्टता बनाए रखने का सतत प्रयास करते हैं। चाहे व्यक्ति, व्यवसाय या संस्थान — हमारा लक्ष्य है ऐसे भरोसेमंद और कुशल समाधान देना जो दीर्घकालिक मूल्य बनाएँ। हम अपने ग्राहकों और पार्टनर्स के निरंतर भरोसे के लिए धन्यवाद देते हैं।",
      contactUs: "संपर्क करें",
      contactUsD: "व्यवसायिक पूछताछ, साझेदारी या सहायता के लिए हमारे आधिकारिक संचार चैनलों के माध्यम से संपर्क करें। हम आमतौर पर 24 घंटे में उत्तर देते हैं।",
      contactBtn: "NakshaCart से संपर्क करें",
      talkBtn: "विशेषज्ञ से बात करें",
      brandFooter: "NakshaCart — एक ब्रांड,",
      directorH: "हमारे डायरेक्टर से मिलिए",
      directorName: "अनिल कुमार सहानी",
      directorRole: "डायरेक्टर, NakshaCart",
      directorBio: "अनिल कुमार सहानी NakshaCart के डायरेक्टर हैं और उन्हें इंजीनियरिंग, कंस्ट्रक्शन मैनेजमेंट और बड़े पैमाने की परियोजना निष्पादन में 18 वर्षों से अधिक का अनुभव है — भारत और अंतर्राष्ट्रीय बाज़ारों दोनों में। उन्होंने Saudi Aramco, Reliance Industries और Hitachi Germany जैसे प्रतिष्ठित वैश्विक क्लाइंट्स की परियोजनाओं पर सफलतापूर्वक काम किया है।",
      directorIntl: "उन्हें मूल्यवान अंतर्राष्ट्रीय अनुभव भी है — सऊदी अरब में जटिल औद्योगिक, फैब्रिकेशन और इन्फ़्रास्ट्रक्चर परियोजनाओं पर काम किया है। यह वैश्विक एक्सपोज़र NakshaCart को आधुनिक मानकों, कुशल निष्पादन और गुणवत्ता-आधारित प्रथाओं के साथ पेशेवर बढ़त देता है।",
      directorVisionH: "उनकी दृष्टि",
      directorVisionD: "उन्होंने NakshaCart की स्थापना एक आम बाज़ार समस्या के समाधान के लिए की — कई इमारतें विशेषज्ञ निगरानी के बिना बनती हैं, जिससे कमज़ोर नियोजन और सामग्री बर्बादी के कारण ख़राब उपयोगिता, सुरक्षा जोखिम और अनावश्यक खर्च होते हैं।",
      directorVisionD2: "उनका लक्ष्य है — विश्व-स्तरीय सेवाएँ स्ट्रीट मार्केट क़ीमतों पर देना, ताकि पेशेवर निर्माण समाधान हर किसी के लिए किफ़ायती हों।",
      directorTrustH: "उन पर भरोसा क्यों",
      directorTrustIntro: "ग्राहक अनिल कुमार सहानी पर भरोसा करते हैं उनके इन गुणों के लिए:",
      directorT1: "मज़बूत पेशेवर और अंतर्राष्ट्रीय अनुभव",
      directorT2: "सिद्ध उद्योग नेतृत्व",
      directorT3: "गहन तकनीकी ज्ञान",
      directorT4: "ईमानदार और पारदर्शी दृष्टिकोण",
      directorT5: "सुरक्षा, गुणवत्ता और लागत बचत पर ध्यान",
      directorMsgH: "डायरेक्टर का संदेश",
      directorMsg: "NakshaCart का उद्देश्य है — विशेषज्ञ मार्गदर्शन और भरोसेमंद सेवा के माध्यम से हर परियोजना को सुरक्षित, उपयोगी, सुंदर और किफ़ायती बनाना।",
    },
    build: {
      chip: "एंड-टू-एंड घर निर्माण",
      heroTitleA: "अपने सपनों का घर बनाएँ",
      heroTitleB: "तय बजट में",
      heroSub: "योजना, डिज़ाइन, स्वीकृति, निर्माण और हैंडओवर — सब कुछ एक ही फिक्स्ड-प्राइस अनुबंध में। 10 साल की स्ट्रक्चरल वारंटी। 470+ क्वालिटी चेकपॉइंट। कोई अनपेक्षित बढ़ोत्तरी नहीं।",
      ctaCalc: "तुरंत लागत अनुमान पाएँ",
      ctaTalk: "बिल्ड सलाहकार से बात करें",
      stat1: "घर डिलीवर किए", stat2: "क्वालिटी चेकपॉइंट",
      stat3: "स्ट्रक्चरल वारंटी", stat4: "समय पर हैंडओवर",
      warranty: "10 वर्ष वारंटी", warrantySub: "स्ट्रक्चरल कवर",
      heroQuote: "\"फिक्स्ड क़ीमत रुपये तक स्थिर रही। दो हफ़्ते पहले समय पर घर मिला।\"",
      heroQuoteAuthor: "अरविंद एस. · कोयंबटूर",
      whyEyebrow: "क्यों NakshaCart Build",
      whyTitle: "निर्माण प्रक्रिया, अब आपके पक्ष में",
      whySub: "मालिक-संचालित अधिकांश निर्माण 20% बजट से अधिक और 3 महीने देरी से चलते हैं। हम इसे दस्तावेज़ीकरण, जवाबदेही और एक ही संपर्क बिंदु से बदल देते हैं।",
      vp1Title: "10 साल की स्ट्रक्चरल वारंटी",
      vp1Body: "हमारा बनाया हर घर RCC फ्रेम और वॉटरप्रूफिंग पर लिखित वारंटी के साथ आता है।",
      vp2Title: "फिक्स्ड-प्राइस अनुबंध",
      vp2Body: "कोई अप्रत्याशित बढ़ोत्तरी नहीं। आप जिस संख्या पर हस्ताक्षर करते हैं, वही भुगतान करते हैं — BOQ चरण पर लॉक।",
      vp3Title: "470+ क्वालिटी चेकपॉइंट",
      vp3Body: "मिट्टी परीक्षण से लेकर अंतिम पेंट तक, हर चरण को दस्तावेज़ीकृत चेकलिस्ट के अनुसार जाँचा जाता है।",
      vp4Title: "वेरिफाइड कॉन्ट्रैक्टर नेटवर्क",
      vp4Body: "केवल वही कॉन्ट्रैक्टर जो पहले प्रोजेक्ट डिलीवर कर चुके हैं और जिनके लाइसेंस सत्यापित हैं — उन्हें ही आपके निर्माण से जोड़ा जाता है।",
      pkgEyebrow: "निर्माण पैकेज",
      pkgTitle: "तीन पैकेज। एक तय क़ीमत।",
      pkgSub: "सब कुछ शामिल — प्रति वर्ग फीट दर। स्पेसिफिकेशन चुनें, हम क़ीमत लॉक करते हैं, कोई आश्चर्य नहीं।",
      pkgEstimate: "अपने घर का अनुमान लगाएँ",
      pkgPerSqft: "/वर्ग फीट",
      pkgRateNote: "सब कुछ शामिल · टियर-1 शहर बेस दर",
      pkgEstimateWith: "{name} के साथ अनुमान",
      classicName: "क्लासिक", premiumName: "प्रीमियम", luxuryName: "लग्ज़री",
      classicTag: "स्मार्ट शुरुआत", premiumTag: "सबसे चुना गया", luxuryTag: "टॉप-टियर",
      classicHeadline: "मज़बूत निर्माण, समझदार स्पेसिफिकेशन",
      premiumHeadline: "लागत और फ़िनिश का सर्वोत्तम संतुलन",
      luxuryHeadline: "आयातित, ऑटोमेटेड, बेदाग़",
      classicI1: "RCC फ्रेम स्ट्रक्चर (M25 / Fe 500)",
      classicI2: "ब्रांडेड विट्रीफाइड फ्लोरिंग (काजरिया / सोमानी)",
      classicI3: "स्टैंडर्ड CP और सैनिटरीवेयर (सेरा)",
      classicI4: "ऐक्रेलिक एमल्शन इंटीरियर पेंट",
      classicI5: "सिंगल-पोल मॉड्यूलर स्विच",
      premiumI1: "प्रीमियम फिटिंग (जैक्वार / हिंदवेयर)",
      premiumI2: "लिविंग + मास्टर में डिज़ाइनर वॉल टेक्सचर",
      premiumI3: "सॉफ्ट-क्लोज़ ड्रॉर्स के साथ मॉड्यूलर किचन",
      premiumI4: "स्मार्ट इलेक्ट्रिकल पॉइंट (USB + AC प्री-वायर)",
      premiumI5: "गीले क्षेत्रों में एंटी-स्किड प्रीमियम टाइलें",
      luxuryI1: "लिविंग + डाइनिंग में आयातित इटैलियन मार्बल",
      luxuryI2: "प्रीमियम जर्मन फिटिंग (ग्रोहे / कोहलर)",
      luxuryI3: "होम ऑटोमेशन: लाइट, लॉक, कैमरे",
      luxuryI4: "कोव लाइटिंग के साथ डिज़ाइनर फॉल्स सीलिंग",
      luxuryI5: "VRV/VRF AC प्री-इंस्टॉलेशन",
      processEyebrow: "हमारी प्रक्रिया",
      processTitle: "प्लॉट से चाबी तक — चार चरणों में",
      p1Title: "योजना और कोटेशन", p1Body: "अपने प्लॉट का विवरण साझा करें, पैकेज चुनें — 48 घंटे में पूरे BOQ के साथ फिक्स्ड-प्राइस कोटेशन पाएँ।", p1Days: "दिन 1 – 7",
      p2Title: "डिज़ाइन और स्वीकृति", p2Body: "हमारे आर्किटेक्ट सभी ड्रॉइंग्स अंतिम रूप देते हैं — वास्तु, स्ट्रक्चर, MEP — और स्थानीय स्वीकृतियाँ आपकी ओर से संभालते हैं।", p2Days: "सप्ताह 2 – 6",
      p3Title: "निर्माण", p3Body: "रोज़ाना साइट अपडेट, साप्ताहिक फ़ोटो रिपोर्ट और मील-पत्थर आधारित भुगतान। आप हर रुपया काम पर देखते हैं।", p3Days: "महीना 2 – 8",
      p4Title: "हैंडओवर और वारंटी", p4Body: "स्नैग लिस्ट साफ़, कंप्लीशन सर्टिफिकेट जारी — और आप 10 साल की स्ट्रक्चरल कवर के साथ घर में आते हैं।", p4Days: "महीना 9",
      calcEyebrow: "लागत अनुमानक",
      calcTitle: "जानिए आपके घर की लागत — सिर्फ़ 60 सेकंड में",
      calcSub: "अपना शहर चुनें, प्लॉट के माप दर्ज करें, पैकेज चुनें — स्ट्रक्चर, फ़िनिशिंग और MEP लागत अलग-अलग के साथ पारदर्शी ब्रेकडाउन पाएँ। लॉगिन ज़रूरी नहीं।",
      calcOpen: "कैलकुलेटर खोलें",
      calcMeta: "मुफ़्त · लॉगिन नहीं · तुरंत ब्रेकडाउन",
      tEyebrow: "घर के मालिकों से",
      tTitle: "बना। डिलीवर हुआ। बजट में।",
      tm1Name: "राजेश और मीना कुलकर्णी", tm1City: "पुणे · 30×40 G+1", tm1Quote: "दो स्थानीय कॉन्ट्रैक्टर्स ने 18% ज़्यादा बताया था। NakshaCart का फिक्स्ड-प्राइस अनुबंध रुपये तक स्थिर रहा — और साप्ताहिक साइट रिपोर्ट से चिंता दूर रही।",
      tm2Name: "अरविंद सुब्रमण्यम", tm2City: "कोयंबटूर · 40×60 G+2", tm2Quote: "प्रीमियम पैकेज चुना। टीम ने DTCP स्वीकृतियाँ एंड-टू-एंड संभालीं, जिनसे मैं डर रहा था। वादे से दो हफ़्ते पहले शिफ़्ट हो गया।",
      tm3Name: "प्रिया और संदीप भाटिया", tm3City: "गुड़गाँव · 50×80 विला", tm3Quote: "आर्किटेक्ट ने सच में सुना। डिज़ाइन चरण में मास्टर सुइट लेआउट दो बार बदला — कोई पुशबैक नहीं, कोई छुपा शुल्क नहीं। अंतिम बिल्ड बिल्कुल रेंडरिंग्स जैसा है।",
      ctaTitle1: "पहले से प्लॉट है?",
      ctaTitle2: "आज ही अपने निर्माण की क़ीमत तय करें।",
      ctaSub: "अपने प्लॉट के बारे में कुछ बातें बताएँ — हम 48 घंटे में बिना बाध्यता वाला BOQ भेजेंगे, और एक बिल्ड सलाहकार आपको हर लाइन समझाएगा।",
      ctaP1: "मुफ़्त साइट विज़िट शामिल",
      ctaP2: "अनुबंध पर साइन करने तक कोई बाध्यता नहीं",
      ctaP3: "किसी भी अन्य कोटेशन से लाइन-दर-लाइन तुलना करें",
      faqEyebrow: "अक्सर पूछे जाने वाले",
      faqTitle: "हर कोई पूछता है ये सवाल",
      faq1Q: "अगर सामग्री की दरें बदलें तो लागत कैसे तय रहती है?",
      faq1A: "हमारा BOQ अनुबंध हस्ताक्षर पर पैकेज और स्कोप अनुसार प्रति वर्ग फीट दरें लॉक कर देता है। सीमेंट, स्टील और मज़दूरी की लागत में बदलाव हम सहन करते हैं — यही फिक्स्ड-प्राइस अनुबंध का मतलब है। केवल मालिक-संचालित स्कोप जोड़ ही बदल सकते हैं, और हर एक को आप लिखित में स्वीकृति देते हैं।",
      faq2Q: "प्रति वर्ग फीट दर में वास्तव में क्या शामिल है?",
      faq2A: "नींव से लेकर रहने योग्य घर तक सब कुछ: स्ट्रक्चर, चिनाई, प्लास्टर, प्लंबिंग और इलेक्ट्रिकल, फ्लोरिंग, दरवाज़े-खिड़कियाँ, किचन काउंटर, बाथरूम फिटिंग, पेंट और बेसिक कंपाउंड वॉल। लिफ्ट, सोलर, मॉड्यूलर किचन अपग्रेड और लैंडस्केपिंग जैसे ऐड-ऑन अलग हैं और BOQ में पारदर्शी रूप से सूचीबद्ध हैं।",
      faq3Q: "क्या आप स्वीकृतियाँ और अनुमतियाँ संभालते हैं?",
      faq3A: "सीधे नहीं, लेकिन बिल्डिंग प्लान स्वीकृति, BWSSB/BESCOM कनेक्शन, मिट्टी परीक्षण रिपोर्ट और कंप्लीशन सर्टिफिकेट के लिए — हम स्वीकृतियों के लिए ज़रूरी ड्रॉइंग्स और दस्तावेज़ देकर सहायता करते हैं। अंतिम स्वीकृतियाँ स्थानीय निकाय नियमों के अनुसार आपको लेनी होती हैं।",
      faq4Q: "एक सामान्य 2,500 sqft G+1 घर बनने में कितना समय लगता है?",
      faq4A: "नींव डालने से हैंडओवर तक लगभग 7–9 महीने, उससे पहले डिज़ाइन और स्वीकृतियों के लिए 4–6 हफ़्ते। अनुबंध समय-सीमा में मानसून की देरी पहले से शामिल होती है।",
      faq5Q: "अगर निर्माण के बीच में बदलाव चाहूँ तो क्या होगा?",
      faq5A: "5% तक स्कोप बदलाव मुफ़्त शामिल है। उसके बाद, काम आगे बढ़ने से पहले आप लागत अंतर के साथ लिखित चेंज ऑर्डर पर हस्ताक्षर करते हैं। कभी कोई आश्चर्यजनक इनवॉइस नहीं।",
      leadMessageLabel: "कुछ और बताना चाहेंगे?",
      leadSubmit: "सबमिट",
    },
    pricingPage: {
      title: "डिज़ाइन और निर्माण के लिए पारदर्शी मूल्य",
      sub: "अपनी ज़रूरत चुनें — डिज़ाइन पैकेज या पूरा निर्माण अनुमान। कोई छुपा शुल्क नहीं, कोई आवर्ती शुल्क नहीं।",
      tabDesign: "डिज़ाइन", tabDesignSub: "ड्रॉइंग्स और प्लान",
      tabConstruction: "निर्माण", tabConstructionSub: "निर्माण लागत अनुमानक",
      sectionTitle: "डिज़ाइन प्लान",
      sectionSub: "एक बार का मूल्य। उतना ही टियर चुनें जितनी डिज़ाइन गहराई आपको चाहिए — साधारण नक्शे से लेकर साइट सहायता वाले पूर्ण स्ट्रक्चरल सेट तक।",
      customisedForPlot: "आपके प्लॉट के अनुसार कस्टमाइज़्ड",
      specialOffer: "छोटे प्लॉट मालिकों के लिए विशेष G+2 होम डिज़ाइन ऑफ़र – 1500 वर्ग फ़ुट तक",
      paymentNote: "बस वह पैकेज चुनें जो आपकी ज़रूरतों के लिए सबसे उपयुक्त हो। भुगतान पूरा होने के बाद, हमारी टीम आपकी परियोजना की ज़रूरतों पर विस्तार से चर्चा करने के लिए आपसे संपर्क करेगी। चिंता न करें — आपका पैसा पूरी तरह सुरक्षित है। यदि डिज़ाइन का काम शुरू होने से पहले आप रद्द करना चाहें तो हम 100% रिफ़ंड नीति प्रदान करते हैं।",
      compare: "तुलना करें",
      popular: "लोकप्रिय", mostPopular: "सबसे लोकप्रिय",
      save: "{pct}% बचाएँ", off: "{pct}% छूट",
      included: "शामिल",
      getStarted: "शुरू करें",
      tierBasic: "बेसिक", tierStandard: "स्टैंडर्ड", tierPopular: "पॉपुलर", tierPremium: "प्रीमियम",
      focusNaksha: "नक्शा", focusArchitectural: "आर्किटेक्चरल", focusConstruction: "निर्माण", focusStructural: "स्ट्रक्चरल",
      validity30: "30 दिन", validity90: "90 दिन", validity365: "365 दिन",
      f2d: "2D प्लान", fElev: "एलिवेशन", f3d: "3D मॉडल",
      fPlumb: "प्लंबिंग ड्रॉइंग", fElec: "इलेक्ट्रिकल ड्रॉइंग",
      fStruct: "स्ट्रक्चरल ड्रॉइंग", fSupport: "ऑन-कॉल साइट सहायता",
      fValidity: "संशोधन वैधता",
      footnote: "मूल्य एक बार के हैं। संशोधन वैधता वह अवधि है जिसमें हम आपकी फ़ीडबैक के अनुसार ड्रॉइंग्स को मुफ़्त संशोधित करते हैं। समाप्ति के बाद संशोधन मानक प्रति घंटे की दर पर लिए जाते हैं।",
      notSure: "किस टियर का चुनाव करें — समझ नहीं आ रहा?",
      notSureSub: "अपने प्लॉट के माप और बजट बताएँ। हमारा डिज़ाइन सलाहकार सही पैकेज सुझाएगा और भीतर क्या है — पूरा समझाएगा।",
      talkAdvisor: "सलाहकार से बात करें",
      browseReady: "तैयार प्लान देखें",
    },
    services: {
      eyebrow: "हमारी सेवाएँ",
      title: "घर बनाने के लिए सब कुछ — एक ही जगह",
      sub: "पहले ड्रॉइंग से लेकर अंतिम चाबी तक — NakshaCart के विशेषज्ञ आपके निर्माण के हर चरण को संभालते हैं।",
      s1Title: "आर्किटेक्चरल डिज़ाइन", s1Sub: "होम प्लान, एलिवेशन व 3D मॉडल",
      s1Desc: "हम आपके प्लॉट के लिए पूरा आर्किटेक्चरल पैकेज तैयार करते हैं — 2D फ्लोर प्लान, सभी एलिवेशन (फ्रंट, पीछे, साइड) और विस्तृत 3D मॉडल — ताकि एक ईंट लगने से पहले ही आप अपने सपनों के घर को देख सकें।",
      s1P1: "2D फ्लोर प्लान (सभी मंज़िलें)", s1P2: "फ्रंट, पीछे व साइड एलिवेशन", s1P3: "3D मॉडल / वॉकथ्रू", s1P4: "वास्तु-अनुकूल लेआउट",
      s2Title: "स्ट्रक्चरल डिज़ाइन", s2Sub: "RCC व नींव की ड्रॉइंग",
      s2Desc: "हमारे स्ट्रक्चरल इंजीनियर आपकी आर्किटेक्चरल योजना के आधार पर पूरा स्ट्रक्चरल ड्रॉइंग सेट तैयार करते हैं — सुरक्षित, कोड-अनुपालन और आपकी स्थानीय मिट्टी के अनुकूल।",
      s2P1: "कॉलम व बीम लेआउट", s2P2: "स्लैब व नींव डिज़ाइन", s2P3: "RCC बार-बेंडिंग शेड्यूल", s2P4: "मिट्टी व लोड विश्लेषण",
      s3Title: "प्लंबिंग ड्रॉइंग", s3Sub: "जल आपूर्ति व ड्रेनेज लेआउट",
      s3Desc: "विस्तृत प्लंबिंग ड्रॉइंग — सभी जल आपूर्ति लाइनें, ड्रेनेज रूटिंग, फिक्स्चर पोज़िशन और पाइप साइज़िंग — ऐसे प्रारूप में जिसे आपका प्लंबर सीधे लागू कर सके।",
      s3P1: "जल आपूर्ति लेआउट", s3P2: "ड्रेनेज व सीवेज रूटिंग", s3P3: "फिक्स्चर व नल पोज़िशन", s3P4: "पाइप साइज़िंग व मटेरियल स्पेसिफिकेशन",
      s4Title: "इलेक्ट्रिकल ड्रॉइंग", s4Sub: "वायरिंग, लोड व स्विच लेआउट",
      s4Desc: "आपके घर के लिए पूरा इलेक्ट्रिकल लेआउट — पॉइंट-वार वायरिंग प्लान, लोड कैलकुलेशन, स्विचबोर्ड पोज़िशन, अर्थिंग और IS मानक अनुपालन।",
      s4P1: "पॉइंट-वार वायरिंग प्लान", s4P2: "लोड कैलकुलेशन शीट", s4P3: "स्विचबोर्ड व DB लेआउट", s4P4: "अर्थिंग व सुरक्षा अनुपालन",
      s5Title: "निर्माण पर्यवेक्षण", s5Sub: "साइट विज़िट व गुणवत्ता जाँच",
      s5Desc: "हमारे पैनलबद्ध सिविल इंजीनियर निर्माण के महत्वपूर्ण चरणों — नींव से स्लैब और फ़िनिश तक — पर साइट विज़िट करते हैं। गुणवत्ता बनाए रखते हुए ग़लतियाँ शुरू में पकड़ी जाती हैं।",
      s5P1: "चरणवार साइट विज़िट", s5P2: "गुणवत्ता व सामग्री जाँच", s5P3: "प्रगति रिपोर्ट", s5P4: "कॉन्ट्रैक्टर समन्वय",
      s6Title: "टर्नकी निर्माण", s6Sub: "एंड-टू-एंड बिल्ड व सामग्री सहायता",
      s6Desc: "अपना प्लॉट सौंपें और तैयार घर में जाएँ। हम डिज़ाइन, ख़रीद, निर्माण और फ़िनिशिंग संभालते हैं — सर्वोत्तम दर पर सामग्री लाकर रहने-योग्य पूरा घर देते हैं।",
      s6P1: "एंड-टू-एंड परियोजना प्रबंधन", s6P2: "सामग्री ख़रीद सहायता", s6P3: "पारदर्शी BOQ व बिलिंग", s6P4: "तय समय की प्रतिबद्धता",
      ctaChip: "शुरू करें",
      ctaTitle: "अपनी ज़रूरत बताइए",
      ctaSub: "अपने प्लॉट का विवरण और चाहिए सेवा साझा करें। हमारे विशेषज्ञ समय-सीमा और कोटेशन के साथ संपर्क करेंगे।",
      ctaP1: "मुफ़्त शुरुआती सलाह",
      ctaP2: "पारदर्शी, प्रतिस्पर्धी मूल्य",
      ctaP3: "लिखित डिलिवरी प्रतिबद्धता",
    },
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("nc_lang") || "en");

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("nc_lang", lang);
  }, [lang]);

  const t = (path, vars) => {
    const parts = path.split(".");
    let cur = dict[lang];
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else return path;
    }
    if (typeof cur === "string" && vars) {
      return cur.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
    }
    return cur;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang: setLangState, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
